import * as React from 'react'
import { Button, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  Control,
  UseFormTrigger,
  UseFormGetValues,
  useWatch,
} from 'react-hook-form'
import shallow from 'zustand/shallow'

import { useCreatePropertyAdStore } from 'store'
import { FormValues } from '.'

interface Props {
  control: Control<FormValues>
  trigger: UseFormTrigger<FormValues>
  getValues: UseFormGetValues<FormValues>
}

function Controls(props: Props) {
  const { trigger, control } = props
  const { t } = useTranslation()

  const watchCategory = useWatch({
    control,
    name: 'step1.category',
  })
  const watchSubCategory = useWatch({
    control,
    name: 'step1.subCategory',
  })

  const [
    currentStep,
    setStep,
    completeStep,
    uncompleteStep,
    validateStep,
    invalidateStep,
    unsetStepInvalid,
    setStepInvalid,
  ] = useCreatePropertyAdStore(
    (state) => [
      state.currentStep,
      state.setStep,
      state.completeStep,
      state.uncompleteStep,
      state.validateStep,
      state.invalidateStep,
      state.unsetStepInvalid,
      state.setStepInvalid,
    ],
    shallow
  )

  React.useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
  }, [currentStep])

  const handleNext = async () => {
    let validStatus
    switch (currentStep) {
      case 0:
        validStatus = await trigger('step1')
        if (validStatus) {
          completeStep('category')
          validateStep('category')
          unsetStepInvalid('category')
          setStep(currentStep + 1)
        } else {
          uncompleteStep('category')
          invalidateStep('category')
          setStepInvalid('category')
        }
        break
      case 1:
        validStatus = await trigger('location', { shouldFocus: true })
        if (validStatus) {
          completeStep('location')
          validateStep('location')
          unsetStepInvalid('location')
          setStep(currentStep + 1)
        } else {
          uncompleteStep('location')
          invalidateStep('location')
          setStepInvalid('location')
        }
        break
      case 2:
        if (!watchCategory || !watchSubCategory) return
        validStatus = await trigger('property', { shouldFocus: true })
        if (validStatus) {
          completeStep('property')
          validateStep('property')
          unsetStepInvalid('property')
          setStep(currentStep + 1)
        } else {
          uncompleteStep('property')
          invalidateStep('property')
          setStepInvalid('property')
        }
        break
      case 3:
        validStatus = await trigger('adPhotos')
        if (validStatus) {
          completeStep('adPhotos')
          validateStep('adPhotos')
          unsetStepInvalid('adPhotos')
          setStep(currentStep + 1)
        } else {
          uncompleteStep('adPhotos')
          invalidateStep('adPhotos')
          setStepInvalid('adPhotos')
        }
        break
      case 4:
        validStatus = await trigger('adAttributes')
        if (validStatus) {
          completeStep('adAttributes')
          validateStep('adAttributes')
          unsetStepInvalid('adAttributes')
          setStep(currentStep + 1)
        } else {
          uncompleteStep('adAttributes')
          invalidateStep('adAttributes')
          setStepInvalid('adAttributes')
        }
        break

      default:
        break
    }
  }
  const handlePrevious = () => {
    switch (currentStep) {
      case 0:
        break
      default:
        setStep(currentStep - 1)
        break
    }
  }

  const CAN_CLICK_PREVIOUS = 0 < currentStep

  return (
    <Box display="flex" marginTop={3} justifyContent={'space-evenly'}>
      {currentStep === 0 ? null : (
        <Button
          disabled={!CAN_CLICK_PREVIOUS}
          variant="outlined"
          onClick={handlePrevious}
          color="fb"
        >
          {t('manageEstateAd.previous')}
        </Button>
      )}

      {currentStep === 4 ? null : (
        <Button variant="contained" onClick={handleNext}>
          {t('manageEstateAd.next')}
        </Button>
      )}
    </Box>
  )
}

export default React.memo(Controls)
